import { createStore } from 'vuex'
import eventBus from '../EventBus'
import SessionService from '../services/SessionService'
import _ from 'lodash'
import constants from '@/constants'
import { signOut } from '@/services/clients/firebase'
import { shutdownIntercom } from '@/utils/intercom'
import { getPayPeriodDateRange, getPickerDatesForDatetimeRange } from '@/utils/PayPeriod'
import restClient from '@/services/clients/rest'
import * as scheduleControls from '@/modules/schedule/scheduleControlsGetters'

const { loginServer } = constants()

// Writes the whole org-user preferences blob: to the server, or -- with no org user, i.e. sudo or
// delegate access, which the endpoint refuses -- to localStorage scoped to this org and person.
async function putOrgUserPreferences (context, preferences) {
  const orgUserId = context.state.orgUserId
  if (!orgUserId) {
    const organizationId = context.state.organizationId
    const userId = context.rootState.userProfile?.userId
    if (organizationId && userId) {
      const key = `org_user_preferences_${organizationId}_${userId}`
      localStorage.setItem(key, JSON.stringify(preferences))
    }
    return preferences
  }
  const response = await restClient.put(`org-users/${orgUserId}/preferences`, { preferences })
  return response.data.preferences
}

// Writes the whole user preferences blob. Unlike org-user preferences there is no localStorage
// fallback: the endpoint is the signed-in person's own, so sudo and delegate access can use it too.
async function putUserPreferences (context, preferences) {
  const userId = context.rootState.userProfile?.userId
  if (!userId) {
    throw new Error('Cannot save user preferences: user ID is not available')
  }
  const response = await restClient.put(`users/${userId}/preferences`, { preferences }, { orgMode: 'none' })
  return response.data.preferences
}

/**
 * Builds a patch action for one of the preference blobs. The endpoints replace the whole blob, so
 * two saves in flight at once can each carry a stale copy of the other's key and whichever lands
 * last wins. Patches therefore go out one at a time, each sending the latest merged state.
 */
function createPreferencesPatcher ({ stateKey, mutation, put, label }) {
  let saveQueue = Promise.resolve()
  let patchCount = 0
  let savesPending = 0
  // What the server last confirmed, to roll back to when a save fails.
  let confirmed = null

  return function patch (context, changes) {
    if (savesPending === 0) {
      confirmed = context.state[stateKey]
    }
    context.commit(mutation, { ...(context.state[stateKey] || {}), ...changes })

    const patchNumber = ++patchCount
    savesPending++

    const run = async () => {
      try {
        // A later patch is queued behind this one and will send everything, this change included.
        if (patchNumber !== patchCount) {
          return
        }
        const saved = await put(context, context.state[stateKey])
        confirmed = saved
        // Only take the server's copy if nothing changed while it was in flight; otherwise it
        // would briefly undo the newer change until that one's save comes back.
        if (patchNumber === patchCount) {
          context.commit(mutation, saved)
        }
      } catch (error) {
        console.warn(`Error saving ${label}`, error)
        if (patchNumber === patchCount) {
          context.commit(mutation, confirmed)
        }
        throw error
      } finally {
        savesPending--
      }
    }

    const result = saveQueue.then(run)
    // Keep the queue going after a failure; the failure is reported through `result`.
    saveQueue = result.catch(() => {})
    return result
  }
}

const store = createStore({
  state () {
    return {
      route: null,
      pendingRoute: null,
      // Track original subscription state, since we convert in-app
      // to free trial in the middle of the billing wizard before payment.
      // It's not really accurate when signing up for a new subscription when
      // the state loaded with a different subscription. But it's good enough.
      originalSubscriptionState: null,
      settingsLoaded: false,
      organizationId: null,
      organizationName: null,
      organizationActive: false,
      orgUserId: null,
      // When this person got a Fareclock login. Used to tell a Classic migrator apart from a
      // brand-new admin. Null only for the system pseudo-user.
      userCreated: null,
      contactSuppressions: null,
      avatar: null,
      subscription: null,
      subscriptionState: null,
      billingCustomerId: null,
      expireDate: null,
      features: [],
      maxDevices: 0,
      maxUsers: 0,
      maxUsersIsWorkersOnly: false,
      adminPersonalDeviceModeAllowed: false,
      activeDevices: 0,
      activeUsers: 0,
      activeWorkers: 0,
      timezone: null,
      // 1-7 is Su-Sa (Organization.week_starts_on).
      weekStartsOn: 1,
      geoEnabled: false,
      bgGeoEnabled: false,
      enableSubmitMissedPunch: false,
      qrCodeEnabled: false,
      nfcTagEnabled: false,
      applyShiftOverlapTo: null,
      payPeriodType: null,
      payPeriodStarts: null,
      usePayClass: null,
      timeOffEnabled: false,
      timeOffAccrualEnabled: false,
      payrollId: null,
      enablePayRun: false,
      enableNetPay: false,
      costingMode: null,
      jobRequired: null,
      shiftMode: null,
      openShiftType: null,
      openShiftStartsAt: null,
      openShiftMaxDuration: null,
      enableShiftExtendRequests: false,
      maxExtendShiftEarly: null,
      maxExtendShiftLate: null,
      enableSchedulePublish: false,
      budgetSeverity: 'off',
      enablePeriodApproval: false,
      periodLockLevel: 'full',
      budgetScopeDimension: 'org',
      approvalPeriodType: 'week',
      approvalPeriodAnchor: null,
      punchCodeMode: null,
      permissions: [],
      employeeSensitiveFields: [],
      adminAllowLabels: null,
      requireJobAccess: false,
      canRestrictJobAccessByUser: false,
      entryStateEnabled: false,
      entryStateExportPrompt: false,
      credentialsEnabled: false,
      fatalError: false,
      fatalErrorTitle: null,
      fatalErrorMsg: null,
      maxDocumentsPerUser: 0,
      maxDocumentFileSize: 2 * Math.pow(10, 6), // 2 MB
      disciplineEnabled: false,
      scheduledReportMaxAdditionalRecipients: 5,
      enableBriefings: false,
      distributor: null,
      maxCrossIndexFields: 5,
      maxCustomFieldsPerModel: 15,
      maxCustomItemsPerField: 500,
      orgPreferences: {},
      orgUserPreferences: {},
      userPreferences: {}
    }
  },
  getters: {
    noConsoleAccess: state => _.isEqual(state.permissions, ['setup_clocks']),
    scheduleEnabled: (state, getters) => getters.hasScheduleFeature && !!state.shiftMode && state.shiftMode !== 'default',
    hasCredentialFeature: state => state.features.includes('credential'),
    hasCredentialAiFeature: state => state.features.includes('credential_ai'),
    hasDocumentFeature: state => state.features.includes('document'),
    hasDisciplineFeature: state => state.features.includes('discipline'),
    hasEntryStateFeature: state => state.features.includes('entry_state'),
    hasFormFeature: state => state.features.includes('form'),
    hasFormConditionFeature: state => state.features.includes('form_condition'),
    hasFormManagerFeature: state => state.features.includes('form_manager'),
    hasFormPdfFeature: state => state.features.includes('form_pdf'),
    hasIntegrationFeature: state => state.features.includes('integration'),
    hasNotificationFeature: state => state.features.includes('notification'),
    hasScheduleFeature: state => state.features.includes('schedule'),
    hasScheduleControlsFeature: scheduleControls.hasScheduleControlsFeature,
    hasNetPayFeature: state => state.features.includes('netpay'),
    hasPayRunFeature: state => state.features.includes('pay_run'),
    hasPayrollFeature: state => state.features.includes('payroll'),
    hasTimeOffFeature: state => state.features.includes('pto'),
    hasTimeOffAccrualFeature: state => state.features.includes('time_off_accrual'),
    hasCostingFeature: state => state.features.includes('job_costing'),
    hasPunchCodeFeature: state => state.features.includes('punch_codes'),
    hasBriefingFeature: state => state.features.includes('briefing'),
    entryStateEnabled: (state, getters) => getters.hasEntryStateFeature && state.entryStateEnabled,
    geoEnabled: (state, getters) => getters.hasGeoFeature && state.geoEnabled,
    bgGeoEnabled: (state, getters) => getters.geoEnabled && getters.hasBgGeoFeature && !!state.bgGeoEnabled,
    netPayEnabled: (state, getters) => getters.hasNetPayFeature && state.enableNetPay,
    payRunEnabled: (state, getters) => getters.hasPayRunFeature && state.enablePayRun,
    payrollEnabled: (state, getters) => getters.hasPayrollFeature && state.usePayClass,
    timeOffEnabled: (state, getters) => getters.hasTimeOffFeature && state.timeOffEnabled,
    timeOffAccrualEnabled: (state, getters) => getters.hasTimeOffAccrualFeature && state.timeOffAccrualEnabled,
    costingEnabled: (state, getters) => getters.hasCostingFeature && !!state.costingMode,
    enhancedCostingEnabled: (state, getters) => getters.costingEnabled && state.costingMode === 'enhanced',
    punchCodesEnabled: (state, getters) => getters.hasPunchCodeFeature && state.punchCodeMode !== 'disabled',
    credentialsEnabled: (state, getters) => getters.hasCredentialFeature && !!state.credentialsEnabled,
    hasBgGeoFeature: state => state.features.includes('bg_geo'),
    hasCustomFieldFeature: state => state.features.includes('customfield'),
    hasFaceFeature: state => state.features.includes('face'),
    hasGeoFeature: state => state.features.includes('geo'),
    needsSetup: (state, getters) => getters.isInApp,
    notInApp: state => state.settingsLoaded && state.subscriptionState !== 'in_app',
    // TODO: isFreeTrial is false negative in case where user aborted paid upgrade.
    // TODO: We may need an org boolean field whether user ever had paid subscription.
    isInApp: state => state.settingsLoaded && state.subscriptionState === 'in_app',
    isFreeTrial: state =>
      state.settingsLoaded &&
      (state.subscriptionState === 'trial' || (state.subscriptionState === 'expired' && !state.billingCustomerId)),
    isPaidPlan: state => state.settingsLoaded && state.subscriptionState !== 'trial',
    orgReady: (state, getters) => !getters.needsSetup && state.organizationActive,
    insideOrgNamespace: state => !!_.get(state.route, 'params.orgId'),
    inSignUpWizard: state => ['sign-up-wizard', 'inapp-sign-up-wizard'].includes(_.get(state.route, 'name')),
    isAccountOwner: state => state.permissions.includes('manage_subscription'),
    canAccessEmployeeSensitive: state => state.permissions.includes('access_employee_sensitive'),
    canAccessEmployeeSensitiveField: (state, getters) => field =>
      getters.canAccessEmployeeSensitive || !state.employeeSensitiveFields.includes(field),
    canEditAdministrators: state => state.permissions.includes('edit_org_users'),
    canEditCosting: state => state.permissions.includes('edit_jobs'),
    canEditSelf: state => state.permissions.includes('edit_self'),
    canEditSelfTimeOff: state => state.permissions.includes('edit_self_time_off'),
    canEditDevices: state => state.permissions.includes('edit_clocks'),
    canEditWorkers: state => state.permissions.includes('edit_employees'),
    canEditOrgSettings: state => state.permissions.includes('edit_settings'),
    canEditPunchTimes: state => state.permissions.includes('edit_punches'),
    canEditPunchAttributes: state => state.permissions.includes('edit_punch_attributes'),
    canAccessManagePunches: (state, getters) => getters.canEditPunchTimes || getters.canEditPunchAttributes,
    canManageClockLogs: state => state.permissions.includes('manage_clock_logs'),
    canManagePayroll: state => state.permissions.includes('manage_payroll'),
    canManageSchedule: state => state.permissions.includes('manage_schedule'),
    canApproveSchedule: scheduleControls.canApproveSchedule,
    canManageTimeOff: (state, getters) => state.permissions.includes('manage_pto') && getters.timeOffEnabled,
    canViewAudit: state => state.permissions.includes('view_audit'),
    canViewReports: state => state.permissions.includes('view_reports'),
    canViewWorkerTimeOffPolicy: (state, getters) =>
      getters.timeOffAccrualEnabled && getters.canAccessEmployeeSensitiveField('time_off_policy'),
    canViewWorkerPay: (state, getters) => getters.canAccessEmployeeSensitiveField('pay_rates'),
    // Older servers don't send weekStartsOn; fall back to Sunday.
    weekStartsOn: state => state.weekStartsOn || 1,
    payPeriodSettings: state => payClass => ({
      payPeriodType: payClass ? payClass.payPeriod : state.payPeriodType,
      payPeriodStarts: payClass ? payClass.payPeriodStartsOn : state.payPeriodStarts
    }),
    payPeriodDateRange: (state, getters) => (whichPeriod, refDate, payClass) => {
      const payPeriodSettings = getters.payPeriodSettings(payClass)
      return getPayPeriodDateRange(
        state.timezone,
        payPeriodSettings.payPeriodType,
        payPeriodSettings.payPeriodStarts,
        whichPeriod,
        refDate)
    },
    payPeriodRangeAsPickerDates: (state, getters) => (whichPeriod, refDate, payClass) => {
      const range = getters.payPeriodDateRange(whichPeriod, refDate, payClass)
      return getPickerDatesForDatetimeRange(...range)
    },
    canRestrictJobAccessByUser: (state, getters) =>
      Boolean(getters.costingEnabled && state.canRestrictJobAccessByUser),
    requireJobAccess: (state, getters) =>
      Boolean(getters.canRestrictJobAccessByUser && state.requireJobAccess),
    entryStateExportPrompt: (state, getters) =>
      Boolean(getters.entryStateEnabled && state.entryStateExportPrompt),
    billableUserCount: state => state.maxUsersIsWorkersOnly ? state.activeWorkers : state.activeUsers,
    disciplineEnabled: state => state.features.includes('discipline') && !!state.disciplineEnabled,
    adminAllowAllLabels: state => _.isEmpty(state.adminAllowLabels),
    accessAllOrgUnits: state => _.isEmpty((state.adminAllowLabels || []).filter(label => label.includes(':orgunit:'))),
    accessAllJobs: (state, getters) => !getters.requireJobAccess && _.isEmpty((state.adminAllowLabels || []).filter(label => label.startsWith('work:job:') || label.startsWith('work:label:'))),
    userNotificationsEnabled: (state, getters, rootState) => getters.hasNotificationFeature || rootState.userProfile.userNotificationsEnabled,
    enableBriefings: (state, getters) => getters.hasBriefingFeature && !!state.enableBriefings,
    healthAndSafetyEnabled: (state, getters) => getters.credentialsEnabled || getters.enableBriefings || getters.hasFormFeature,
    isByxbiOrg: state => state.distributor === 5685342286643200,
    canManageByxbi: (state, getters, rootState) => getters.isByxbiOrg && (
      rootState.userProfile.isAppAdmin ||
      rootState.userProfile.isDistributorAdmin ||
      ['staff', 'super'].includes(rootState.userProfile.sudoType)
    ),
    hasPendingRoute: state => !!state.pendingRoute,
    schedulePublishEnabled: (state, getters) => !!state.enableSchedulePublish && getters.hasNotificationFeature,
    scheduleBudgetEnabled: scheduleControls.scheduleBudgetEnabled,
    schedulePeriodApprovalEnabled: scheduleControls.schedulePeriodApprovalEnabled,
    scheduleBudgetsInUse: scheduleControls.scheduleBudgetsInUse
  },
  actions: {
    async login (context, orgId) {
      try {
        await this.getters['jwt/idaasJwt']()
        context.commit('login', orgId)
      } catch (error) {
        if (!error.redirecting) {
          context.commit('fatalErrorDidOccur', {
            fatalErrorMsg: 'Was unable to login due to a network error.'
          })
        }
      }
    },
    logout () {
      shutdownIntercom()
      signOut()
        .finally(() => { window.location.href = `${loginServer}/logout` })
    },
    async initSession (context, orgId) {
      try {
        context.commit('initSession', await SessionService.retrieveSettings(orgId))
      } catch (error) {
        // default error message
        let fatalErrorMsg = 'Was unable to load user settings due to a network error.'
        const response = error.response

        if (_.get(response, 'status') === 403) {
          fatalErrorMsg = 'You do not have permission to access this organization.'
        } else if (_.get(response, 'status') === 401 && _.get(response, 'data.detail')) {
          // example detail: Organization login requirement not met
          fatalErrorMsg = response.data.detail
        }

        context.commit('fatalErrorDidOccur', { fatalErrorMsg })
      }
    },
    updateSession (context, settings) {
      context.commit('updateSession', settings)
    },
    fatalErrorDidOccur (context, { fatalErrorTitle, fatalErrorMsg }) {
      context.commit('fatalErrorDidOccur', { fatalErrorTitle, fatalErrorMsg })
    },
    decrementActiveUserCount (context, wasWorker) {
      context.commit('updateSession', {
        activeUsers: context.state.activeUsers - 1,
        activeWorkers: context.state.activeWorkers - (wasWorker ? 1 : 0)
      })
    },
    setActiveUserCount (context, { activeUsers, activeWorkers }) {
      if (activeUsers !== context.state.activeUsers || activeWorkers !== context.state.activeWorkers) {
        context.commit('updateSession', { activeUsers, activeWorkers })
      }
    },
    setActiveDeviceCount (context, activeDevices) {
      if (activeDevices !== context.state.activeDevices) {
        context.commit('updateSession', { activeDevices })
      }
    },
    async saveOrgUserPreferences (context, preferences) {
      try {
        const saved = await putOrgUserPreferences(context, preferences)
        context.commit('setOrgUserPreferences', saved)
        return saved
      } catch (error) {
        console.warn('Error saving org user preferences', error)
        throw error
      }
    },
    /**
     * Changes some org-user preference keys and leaves the rest alone. Prefer this over
     * saveOrgUserPreferences: the change shows immediately, and concurrent changes to different
     * keys (say, the sidebar layout and pinned reports) cannot overwrite each other.
     *
     * Resolves once this change is stored, or once a later patch has superseded it. On failure
     * the state rolls back to what the server last confirmed, and the promise rejects.
     */
    patchOrgUserPreferences: createPreferencesPatcher({
      stateKey: 'orgUserPreferences',
      mutation: 'setOrgUserPreferences',
      put: putOrgUserPreferences,
      label: 'org user preferences'
    }),
    /**
     * The user-preferences counterpart of patchOrgUserPreferences, for preferences that follow a
     * person across organizations: color scheme, the console tour, What's New. There is no
     * whole-blob save for these: every writer patches, so none can overwrite another's key.
     */
    patchUserPreferences: createPreferencesPatcher({
      stateKey: 'userPreferences',
      mutation: 'setUserPreferences',
      put: putUserPreferences,
      label: 'user preferences'
    })
  },
  mutations: {
    login (state, orgId) {
      this.dispatch('userProfile/load')
      this.dispatch('orgService/bootstrapOrg', orgId)
    },
    initSession (state, session) {
      Object.assign(state, session)

      // The session payload spells the preference blobs in snake_case while the rest of it is
      // camelCase, so a plain Object.assign leaves state.userPreferences / state.orgPreferences
      // at their empty defaults and every server-stored preference silently reverts to its
      // default on load. Map them across explicitly.
      if (session.user_preferences) {
        state.userPreferences = session.user_preferences
      }
      if (session.org_preferences) {
        state.orgPreferences = session.org_preferences
      }
      // Only when the session actually carries org-user preferences -- with no org user (sudo
      // access) the value is null, and the localStorage fallback below owns that case.
      if (session.org_user_preferences) {
        state.orgUserPreferences = session.org_user_preferences
      }

      // TODO: support switching org, or should we just reload page?
      state.originalSubscriptionState = state.subscriptionState
      state.settingsLoaded = true
      eventBus.emit('session-updated', session)
    },
    updateSession (state, session) {
      Object.assign(state, session)
      eventBus.emit('session-updated', state)
    },
    fatalErrorDidOccur (state, { fatalErrorTitle, fatalErrorMsg }) {
      Object.assign(state, {
        fatalError: true,
        fatalErrorTitle: fatalErrorTitle,
        fatalErrorMsg: fatalErrorMsg
      })
    },
    setRoute (state, route) {
      state.route = route
    },
    setPendingRoute (state, route) {
      state.pendingRoute = route
    },
    setUserPreferences (state, preferences) {
      state.userPreferences = preferences
    },
    setOrgUserPreferences (state, preferences) {
      state.orgUserPreferences = preferences
    }
  }
})

// Sync local storage org user preferences when logged in
setTimeout(() => {
  store.watch(
    state => ({
      organizationId: state.organizationId,
      orgUserId: state.orgUserId,
      userId: state.userProfile?.userId
    }),
    ({ organizationId, orgUserId, userId }) => {
      if (!orgUserId && organizationId && userId) {
        const key = `org_user_preferences_${organizationId}_${userId}`
        const localPrefs = localStorage.getItem(key)
        if (localPrefs) {
          try {
            store.commit('setOrgUserPreferences', JSON.parse(localPrefs) || {})
          } catch (e) {
            console.error('Failed to parse org user preferences from localStorage', e)
          }
        }
      }
    },
    { immediate: true }
  )
})

export default store
